import {
  ParsedNotification,
  NotificationType,
  CVNotUpdatedNotificationContent,
  LowCVScoreNotificationContent,
  ExpiringCertificationContent,
  OnBenchUpdateCVNotificationContent,
  PeopleAvailableSoonNotificationContent
} from '../../../types/notification';
import {
  isCVNotUpdatedContent,
  isLowCVScoreContent,
  isExpiringCertificationContent,
  isOnBenchUpdateCVContent,
  isPeopleAvailableSoonContent
} from './notificationParser';

/**
 * Interface for notification message
 */
export interface NotificationMessage {
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  actionText?: string;
  actionUrl?: string;
}

/**
 * Format CV Not Updated notification message
 */
function formatCVNotUpdatedMessage(content: CVNotUpdatedNotificationContent): NotificationMessage {
  const personName = `${content.personFirstName} ${content.personLastName}`;
  const lastUpdateDate = new Date(content.lastUpdateDate).toLocaleDateString();
  
  return {
    title: 'CV Update Required',
    message: `${personName}'s CV hasn't been updated for ${content.daysSinceUpdate} days (last updated: ${lastUpdateDate}). The threshold is ${content.thresholdDays} days.`,
    severity: 'warning',
    actionText: 'Update CV',
    actionUrl: `/person/${content.personId}/cv`
  };
}

/**
 * Format Low CV Score notification message
 */
function formatLowCVScoreMessage(content: LowCVScoreNotificationContent): NotificationMessage {
  const personName = `${content.personFirstName} ${content.personLastName}`;
  const scorePercentage = (content.currentScore * 100).toFixed(0);
  const thresholdPercentage = (content.thresholdScore * 100).toFixed(0);
  
  return {
    title: 'Low CV Score Alert',
    message: `${personName}'s CV score is ${scorePercentage}%, which is below the threshold of ${thresholdPercentage}%. Score deficit: ${content.scoreDeficit.toFixed(0)}%.`,
    severity: 'error',
    actionText: 'Improve CV',
    actionUrl: `/person/${content.personId}/cv`
  };
}

/**
 * Format Expiring Certification notification message
 */
function formatExpiringCertificationMessage(content: ExpiringCertificationContent): NotificationMessage {
  const personName = `${content.personFirstName} ${content.personLastName}`;
  const expirationDate = new Date(content.expirationDate).toLocaleDateString();
  const daysText = content.daysUntilExpiry === 1 ? 'day' : 'days';
  
  return {
    title: 'Certification Expiring Soon',
    message: `${personName}'s certification "${content.certificateTitle}" expires in ${content.daysUntilExpiry} ${daysText} (${expirationDate}).`,
    severity: 'warning',
    actionText: 'View Certifications',
    actionUrl: `/person/${content.personId}/certifications`
  };
}

/**
 * Format On Bench Update CV notification message
 */
function formatOnBenchUpdateCVMessage(content: OnBenchUpdateCVNotificationContent): NotificationMessage {
  const personName = `${content.personFirstName} ${content.personLastName}`;
  const availabilityDate = content.availabilityDate 
    ? new Date(content.availabilityDate).toLocaleDateString()
    : 'soon';
  const lastUpdateText = content.lastCVUpdate 
    ? `Last CV update: ${new Date(content.lastCVUpdate).toLocaleDateString()}`
    : 'CV needs updating';
  
  return {
    title: 'Bench - CV Update Required',
    message: `${personName} is on the bench and available ${availabilityDate}. ${lastUpdateText}. Please ensure CV is up to date.`,
    severity: 'info',
    actionText: 'Update CV',
    actionUrl: `/person/${content.personId}/cv`
  };
}

/**
 * Format People Available Soon notification message
 */
function formatPeopleAvailableSoonMessage(content: PeopleAvailableSoonNotificationContent): NotificationMessage {
  const peopleText = content.peopleCount === 1 ? 'person' : 'people';
  const daysText = content.thresholdDays === 1 ? 'day' : 'days';
  
  let message = `${content.peopleCount} ${peopleText} will be available within ${content.thresholdDays} ${daysText}:`;
  
  // Add details for first few people
  const displayPeople = content.people.slice(0, 3);
  displayPeople.forEach(person => {
    const personName = `${person.personFirstName} ${person.personLastName}`;
    const availabilityDate = person.availabilityDate 
      ? new Date(person.availabilityDate).toLocaleDateString()
      : 'TBD';
    message += `\n• ${personName} (${availabilityDate})`;
  });
  
  if (content.people.length > 3) {
    message += `\n... and ${content.people.length - 3} more`;
  }
  
  return {
    title: 'People Available Soon',
    message,
    severity: 'info',
    actionText: 'View Available People',
    actionUrl: '/people/available'
  };
}

/**
 * Get formatted message for any notification type
 */
export function getNotificationMessage(notification: ParsedNotification): NotificationMessage {
  const { type, content, id } = notification;
  
  // Handle special local CV creation notification
  if (id === -1 && type === NotificationType.CVNotUpdated) {
    return {
      title: 'CV Creation Required',
      message: 'To use all functionalities of the system, please create your own CV. It doesn\'t have to be complete, but it\'s required to have your basic information.',
      severity: 'error',
      actionText: 'Create CV',
      actionUrl: '/cv-editor/new'
    };
  }
  
  try {
    switch (type) {
      case NotificationType.CVNotUpdated:
        if (isCVNotUpdatedContent(content)) {
          return formatCVNotUpdatedMessage(content);
        }
        break;
        
      case NotificationType.LowCVScore:
        if (isLowCVScoreContent(content)) {
          return formatLowCVScoreMessage(content);
        }
        break;
        
      case NotificationType.CertificationExpiringSoon:
        if (isExpiringCertificationContent(content)) {
          return formatExpiringCertificationMessage(content);
        }
        break;
        
      case NotificationType.OnBenchUpdateCV:
        if (isOnBenchUpdateCVContent(content)) {
          return formatOnBenchUpdateCVMessage(content);
        }
        break;
        
      case NotificationType.PeopleAvailableSoon:
        if (isPeopleAvailableSoonContent(content)) {
          return formatPeopleAvailableSoonMessage(content);
        }
        break;
        
      default:
        break;
    }
  } catch (error) {
    console.error('Error formatting notification message:', error, notification);
  }
  
  // Fallback message
  return {
    title: 'Notification',
    message: 'You have a new notification.',
    severity: 'info'
  };
}

/**
 * Get formatted messages for multiple notifications
 */
export function getNotificationMessages(notifications: ParsedNotification[]): NotificationMessage[] {
  return notifications.map(getNotificationMessage);
}

/**
 * Get notification icon based on type
 */
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case NotificationType.CVNotUpdated:
      return '📄';
    case NotificationType.LowCVScore:
      return '📉';
    case NotificationType.CertificationExpiringSoon:
      return '🏆';
    case NotificationType.OnBenchUpdateCV:
      return '🪑';
    case NotificationType.PeopleAvailableSoon:
      return '👥';
    default:
      return '🔔';
  }
}

/**
 * Get notification color based on severity
 */
export function getNotificationColor(severity: NotificationMessage['severity']): string {
  switch (severity) {
    case 'error':
      return '#ff4757';
    case 'warning':
      return '#ffa502';
    case 'success':
      return '#2ed573';
    case 'info':
    default:
      return '#3742fa';
  }
}

/**
 * Check if notification is urgent (error or warning with short time)
 */
export function isUrgentNotification(notification: ParsedNotification): boolean {
  const message = getNotificationMessage(notification);
  
  if (message.severity === 'error') {
    return true;
  }
  
  if (message.severity === 'warning') {
    const { type, content } = notification;
    
    // Check for urgent conditions
    if (type === NotificationType.CertificationExpiringSoon && isExpiringCertificationContent(content)) {
      return content.daysUntilExpiry <= 7; // Urgent if expiring within a week
    }
    
    if (type === NotificationType.CVNotUpdated && isCVNotUpdatedContent(content)) {
      return content.daysSinceUpdate >= content.thresholdDays * 1.5; // Urgent if significantly overdue
    }
  }
  
  return false;
} 